"use client";

/**
 * SkinProvider — applies the active whitelabel skin to the shell at runtime by injecting
 * the skin's CSS (buildSkinCss) and setting <html data-skin> + the mode class.
 *
 * Phase 1 (dev): the skin is chosen via a `?skin=<id>` query param and remembered in
 * sessionStorage, so the whole platform can be previewed in any skin without server
 * plumbing. Phase 2 binds the skin to the org/whitelabel server-side (SSR, no flash).
 */
import { useEffect } from "react";
import { getSkin, type ThemeConfig } from "@/lib/whitelabel/skins";
import { buildSkinCss } from "@/lib/whitelabel/build-skin-css";

const STYLE_ID = "qod-skin-style";
const STORE_KEY = "qod_skin";
// Cookies the root layout reads to SSR the skin (mirror of src/lib/whitelabel/ssr-skin.ts,
// which is server-only and can't be imported here). Keeping these in sync with the resolved
// binding is what makes a reload flash-free.
const SKIN_COOKIE = "qod_skin";
const SKIN_CFG_COOKIE = "qod_skin_cfg";
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60;
const CFG_COOKIE_LIMIT = 3500; // stay well under the ~4KB cookie ceiling

function writeCookie(name: string, value: string) {
  try { document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`; } catch { /* ignore */ }
}
function clearCookie(name: string) {
  try { document.cookie = `${name}=; path=/; max-age=0; SameSite=Lax`; } catch { /* ignore */ }
}
/** Persist the applied skin so the next SSR (root layout) renders it before first paint. */
function persistSkin(id: string | null, configJson: string | null) {
  if (!id || id === "default") { clearCookie(SKIN_COOKIE); clearCookie(SKIN_CFG_COOKIE); return; }
  writeCookie(SKIN_COOKIE, id);
  // A registry skin is rebuilt from its id server-side; only a custom (wl-*) config needs the
  // JSON — and only if it fits in a cookie (huge base64 backgrounds fall back to the client seed).
  if (configJson && encodeURIComponent(configJson).length < CFG_COOKIE_LIMIT) writeCookie(SKIN_CFG_COOKIE, configJson);
  else clearCookie(SKIN_CFG_COOKIE);
}

/** Apply a full (AI-authored) ThemeConfig — buildSkinCss on the client, then set data-skin. */
export function applySkinConfig(config: ThemeConfig) {
  if (typeof document === "undefined" || !config?.id) return;
  const root = document.documentElement;
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = buildSkinCss(config);
  root.setAttribute("data-skin", config.id);
  root.classList.toggle("dark", config.mode === "dark");
}

export function applySkin(skinId: string | null | undefined) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const skin = getSkin(skinId);
  let el = document.getElementById(STYLE_ID) as HTMLStyleElement | null;

  if (!skin || skin.id === "default") {
    root.removeAttribute("data-skin");
    if (el) el.textContent = "";
    return;
  }
  if (!el) {
    el = document.createElement("style");
    el.id = STYLE_ID;
    document.head.appendChild(el);
  }
  el.textContent = buildSkinCss(skin);
  root.setAttribute("data-skin", skin.id);
  root.classList.toggle("dark", skin.mode === "dark");
}

export function SkinProvider() {
  useEffect(() => {
    const read = () => {
      // Precedence: an explicit ?skin= preview wins; otherwise the org's whitelabel binding
      // (seeded server-side by the (app) layout) — a full custom config JSON if present, else a
      // registry skin id; otherwise keep whatever the server already SSR'd (cookie), then a sticky preview.
      const seedEl = document.getElementById("qod-skin-seed");
      const hasSeed = !!seedEl; // present on every (app) page — even when the org is on the default look
      const seed = seedEl?.getAttribute("data-skin-id") || "";
      const seedConfig = seedEl?.getAttribute("data-skin-config") || null;
      // A seed that must NOT outlive this page. An unpublished design being
      // previewed is seeded exactly like a published one — that sameness is
      // the point, it is what stops the preview drifting — but persisting it
      // would carry a candidate nobody approved into the admin's cookie, and
      // from there into every other tab and their next visit.
      const ephemeral = seedEl?.getAttribute("data-skin-ephemeral") === "1";
      let preview: string | null = null;
      try {
        preview = new URLSearchParams(window.location.search).get("skin");
        if (preview != null) sessionStorage.setItem(STORE_KEY, preview);
      } catch {
        /* ignore */
      }
      if (preview != null) {
        applySkin(preview); // registry preview overrides the binding (ephemeral — not persisted to the cookie)
        return;
      }
      if (hasSeed) {
        // The (app) layout is authoritative for a logged-in org: apply its binding and keep
        // the SSR cookie in sync so the next reload paints this skin with no flash.
        if (seedConfig) {
          try {
            const cfg = JSON.parse(seedConfig) as ThemeConfig;
            applySkinConfig(cfg);
            if (!ephemeral) persistSkin(cfg.id, seedConfig);
            return;
          } catch {
            /* fall through to id */
          }
        }
        if (seed) { applySkin(seed); if (!ephemeral) persistSkin(seed, null); return; }
        applySkin(null); persistSkin(null, null); // seed present but empty → org is on the default look
        return;
      }
      // No seed → not an (app) page (login/invite/etc.). Don't clobber a skin the root layout
      // already SSR'd from the cookie; only fall back to a sticky preview when nothing is applied.
      const current = document.documentElement.getAttribute("data-skin");
      if (!current) {
        let sticky: string | null = null;
        try { sticky = sessionStorage.getItem(STORE_KEY); } catch { /* ignore */ }
        if (sticky) applySkin(sticky);
      }
    };
    read();
    window.addEventListener("popstate", read);
    return () => window.removeEventListener("popstate", read);
  }, []);
  return null;
}
